import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PhotoGalleryModal } from '../components/PhotoGalleryModal';
import { MapPin, ShieldCheck, Wifi, Zap, Droplets, Lock, Utensils, BedDouble, Maximize, Users, Star, Share2, Heart, ChevronRight } from 'lucide-react';
// Mock data - in a real app this would come from an API based on ID
const hostelData = {
  id: 1,
  name: 'Emerald Heights',
  location: 'Unilag, Akoka (5 min walk to gate)',
  price: '₦450,000',
  period: '/session',
  type: 'Self-contain',
  verified: true,
  rating: 4.8,
  reviews: 24,
  description: 'Experience premium student living at Emerald Heights. This fully serviced self-contain apartment offers the perfect balance of privacy and community. Recently renovated with modern fittings, 24/7 power supply via inverter backup, and high-speed internet access.',
  images: ['https://images.unsplash.com/photo-1555854877-bab0e564b8d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1484154218962-a1c002085d2f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80' // Exterior
  ],
  captions: ['Spacious Bedroom with Wardrobe', 'Modern Kitchen with Cabinets', 'En-suite Bathroom', 'Study Area', 'Kitchen Storage', 'Building Exterior'],
  amenities: {
    utilities: [{
      name: '24/7 Power',
      icon: Zap
    }, {
      name: 'Running Water',
      icon: Droplets
    }, {
      name: 'Free WiFi',
      icon: Wifi
    }],
    security: [{
      name: 'Gated Security',
      icon: Lock
    }, {
      name: 'CCTV Cameras',
      icon: ShieldCheck
    }],
    facilities: [{
      name: 'Kitchen',
      icon: Utensils
    }, {
      name: 'Study Room',
      icon: Maximize
    }]
  },
  specs: {
    size: '25 sqm',
    beds: '1 Double Bed',
    occupancy: 'Max 2 Students'
  }
};
export function HostelDetailPage() {
  const {
    id
  } = useParams();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };
  return <div className="min-h-screen bg-slate-50 pt-20" data-id="element-612">
      <Navigation data-id="element-613" />

      <PhotoGalleryModal isOpen={isGalleryOpen} onClose={() => setIsGalleryOpen(false)} images={hostelData.images} captions={hostelData.captions} initialIndex={galleryIndex} data-id="element-614" />

      {/* Hero Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-615">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-[400px] md:h-[500px]" data-id="element-616">
          {/* Main Image */}
          <div className="md:col-span-3 h-full relative rounded-2xl overflow-hidden cursor-pointer group" onClick={() => openGallery(0)} data-id="element-617">
            <img src={hostelData.images[0]} alt="Main view" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-618" />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" data-id="element-619" />
            <Button className="absolute bottom-4 right-4 bg-white/90 text-slate-900 hover:bg-white" onClick={e => {
            e.stopPropagation();
            openGallery(0);
          }} data-id="element-620">
              View All Photos
            </Button>
          </div>

          {/* Side Thumbnails */}
          <div className="hidden md:flex flex-col gap-4 h-full" data-id="element-621">
            {hostelData.images.slice(1, 4).map((img, idx) => <div key={idx} className="flex-1 relative rounded-xl overflow-hidden cursor-pointer group" onClick={() => openGallery(idx + 1)} data-id="element-622">
                <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-623" />
                {idx === 2 && hostelData.images.length > 4 && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-lg" data-id="element-624">
                    +{hostelData.images.length - 4} more
                  </div>}
              </div>)}
          </div>
        </div>
      </div>

      <Section className="!py-8" data-id="element-625">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-626">
          {/* Main Content */}
          <div className="flex-1" data-id="element-627">
            {/* Header Info */}
            <div className="mb-8" data-id="element-628">
              <div className="flex flex-wrap gap-3 mb-4" data-id="element-629">
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-630">
                  <ShieldCheck className="w-4 h-4 mr-1" data-id="element-631" /> Verified Property
                </Badge>
                <Badge variant="neutral" className="px-3 py-1 text-sm" data-id="element-632">
                  {hostelData.type}
                </Badge>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-id="element-633">
                {hostelData.name}
              </h1>

              <div className="flex items-center text-slate-600 mb-4" data-id="element-634">
                <MapPin className="w-5 h-5 mr-2 text-amber-500" data-id="element-635" />
                {hostelData.location}
              </div>

              <div className="flex items-center gap-4 text-sm" data-id="element-636">
                <div className="flex items-center text-amber-500 font-bold" data-id="element-637">
                  <Star className="w-4 h-4 fill-current mr-1" data-id="element-638" />
                  {hostelData.rating} ({hostelData.reviews} reviews)
                </div>
                <button className="text-slate-500 hover:text-slate-900 flex items-center" data-id="element-639">
                  <Share2 className="w-4 h-4 mr-1" data-id="element-640" /> Share
                </button>
                <button className="text-slate-500 hover:text-red-500 flex items-center" data-id="element-641">
                  <Heart className="w-4 h-4 mr-1" data-id="element-642" /> Save
                </button>
              </div>
            </div>

            <div className="h-px bg-slate-200 mb-8" data-id="element-643" />

            {/* Description */}
            <div className="mb-10" data-id="element-644">
              <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-645">
                About this place
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg" data-id="element-646">
                {hostelData.description}
              </p>
            </div>

            {/* Specs */}
            <div className="grid grid-cols-3 gap-4 mb-10" data-id="element-647">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-648">
                <Maximize className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-649" />
                <div className="font-bold text-slate-900" data-id="element-650">
                  {hostelData.specs.size}
                </div>
                <div className="text-xs text-slate-500" data-id="element-651">Room Size</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-652">
                <BedDouble className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-653" />
                <div className="font-bold text-slate-900" data-id="element-654">
                  {hostelData.specs.beds}
                </div>
                <div className="text-xs text-slate-500" data-id="element-655">Bed Type</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-656">
                <Users className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-657" />
                <div className="font-bold text-slate-900" data-id="element-658">
                  {hostelData.specs.occupancy}
                </div>
                <div className="text-xs text-slate-500" data-id="element-659">Occupancy</div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-10" data-id="element-660">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-661">
                What this place offers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12" data-id="element-662">
                {Object.entries(hostelData.amenities).map(([category, items]) => items.map((item, idx) => <div key={`${category}-${idx}`} className="flex items-center gap-3 text-slate-700" data-id="element-663">
                      <item.icon className="w-6 h-6 text-slate-400" data-id="element-664" />
                      <span className="text-lg" data-id="element-665">{item.name}</span>
                    </div>))}
              </div>
            </div>

            {/* Reviews Preview */}
            <div className="mb-10" data-id="element-666">
              <div className="flex justify-between items-center mb-6" data-id="element-667">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-668">Reviews</h2>
                <Link to="#" className="text-amber-600 font-medium hover:underline" data-id="element-669">
                  View all {hostelData.reviews} reviews
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-670">
                {[1, 2].map(i => <div key={i} className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-671">
                    <div className="flex items-center gap-3 mb-3" data-id="element-672">
                      <div className="w-10 h-10 bg-slate-200 rounded-full" data-id="element-673" />
                      <div data-id="element-674">
                        <div className="font-bold text-slate-900" data-id="element-675">
                          Student Name
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-676">
                          October 2023
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm" data-id="element-677">
                      "Great place to stay! The power supply is actually 24/7 as
                      advertised. Security is tight and the environment is
                      conducive for reading."
                    </p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:w-96 flex-shrink-0" data-id="element-678">
            <div className="sticky top-24" data-id="element-679">
              <Card className="shadow-lg border-slate-200" data-id="element-680">
                <CardContent className="p-6" data-id="element-681">
                  <div className="flex justify-between items-end mb-6" data-id="element-682">
                    <div data-id="element-683">
                      <span className="text-3xl font-bold text-slate-900" data-id="element-684">
                        {hostelData.price}
                      </span>
                      <span className="text-slate-500" data-id="element-685">
                        {hostelData.period}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6" data-id="element-686">
                    <div className="p-3 border border-slate-200 rounded-lg" data-id="element-687">
                      <div className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-688">
                        Move-in Date
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-689">
                        Select Date
                      </div>
                    </div>
                    <div className="p-3 border border-slate-200 rounded-lg" data-id="element-690">
                      <div className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-691">
                        Duration
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-692">
                        1 Session (9 Months)
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mb-3" size="lg" data-id="element-693">
                    Reserve Now
                  </Button>
                  <Button variant="outline" className="w-full mb-6" data-id="element-694">
                    Schedule Tour
                  </Button>

                  <div className="space-y-3 text-sm text-slate-500" data-id="element-695">
                    <div className="flex justify-between" data-id="element-696">
                      <span data-id="element-697">Rent</span>
                      <span data-id="element-698">{hostelData.price}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-699">
                      <span data-id="element-700">Service Charge</span>
                      <span data-id="element-701">₦50,000</span>
                    </div>
                    <div className="flex justify-between" data-id="element-702">
                      <span data-id="element-703">Caution Fee</span>
                      <span data-id="element-704">₦30,000</span>
                    </div>
                    <div className="h-px bg-slate-200 my-2" data-id="element-705" />
                    <div className="flex justify-between font-bold text-slate-900 text-base" data-id="element-706">
                      <span data-id="element-707">Total</span>
                      <span data-id="element-708">₦530,000</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-6 bg-amber-50 p-4 rounded-xl border border-amber-100 flex gap-3" data-id="element-709">
                <ShieldCheck className="w-6 h-6 text-amber-600 flex-shrink-0" data-id="element-710" />
                <div data-id="element-711">
                  <div className="font-bold text-amber-800 text-sm" data-id="element-712">
                    Simba Guarantee
                  </div>
                  <div className="text-xs text-amber-700 mt-1" data-id="element-713">
                    Your payment is held in escrow until you verify the property
                    in person.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>;
}